if (!customElements.get('charm-bar')) {
  class CharmBar extends HTMLElement {
    connectedCallback() {
      this.maxCharms = parseInt(this.dataset.maxCharms, 10) || 3;
      this.charmWidth = parseFloat(this.dataset.charmWidth) || 14;
      this.rotation = parseFloat(this.dataset.rotation) || 0;
      const s = this.dataset.slots.split(',').map(Number);
      this.slots = { left: [s[0], s[1]], center: [s[2], s[3]], right: [s[4], s[5]] };
      this.formatter = new Intl.NumberFormat(this.dataset.locale, {
        style: 'currency',
        currency: this.dataset.currency,
      });

      this.chain = null;
      this.charms = [];

      this.chainImg = this.querySelector('[data-chain-img]');
      this.charmLayer = this.querySelector('[data-charm-layer]');
      this.placeholder = this.querySelector('[data-placeholder]');
      this.discountNote = this.querySelector('[data-discount-note]');
      this.selectionList = this.querySelector('[data-selection]');
      this.totalEl = this.querySelector('[data-total]');
      this.errorEl = this.querySelector('[data-error]');
      this.backBtn = this.querySelector('[data-back]');
      this.nextBtn = this.querySelector('[data-next]');
      this.addBtn = this.querySelector('[data-add]');

      this.addEventListener('click', this.onClick.bind(this));
      this.initFixedSummary();
      this.selectDefaultChain();
      this.render();
    }

    // Preselect the chain chosen in the theme editor (if it is in the grid and available).
    selectDefaultChain() {
      const id = this.dataset.defaultChain;
      if (!id) return;
      const card = this.querySelector(`.charm-bar__card[data-kind="chain"][data-variant-id="${id}"]:not(:disabled)`);
      if (card) this.chain = this.readCard(card);
    }

    disconnectedCallback() {
      this.resizeObserver?.disconnect();
      this.intersectionObserver?.disconnect();
    }

    // The summary is position: fixed. Reserve its height at the bottom of the section
    // and only show it while the builder is on screen (so it never covers the footer).
    initFixedSummary() {
      const summary = this.querySelector('.charm-bar__summary');
      this.resizeObserver = new ResizeObserver(() =>
        this.style.setProperty('--charm-bar-summary-height', `${summary.offsetHeight}px`)
      );
      this.resizeObserver.observe(summary);

      this.intersectionObserver = new IntersectionObserver(([entry]) =>
        this.classList.toggle('is-in-view', entry.isIntersecting)
      );
      this.intersectionObserver.observe(this.querySelector('.charm-bar__layout'));
    }

    onClick(event) {
      const card = event.target.closest('.charm-bar__card');
      if (card && !card.disabled) return card.dataset.kind === 'chain' ? this.selectChain(card) : this.toggleCharm(card);

      const tab = event.target.closest('[data-tab]');
      if (tab) return this.showTab(tab.dataset.tab);

      const remove = event.target.closest('[data-remove]');
      if (remove) return this.removeItem(remove.dataset.remove);

      const stepButton = event.target.closest('[data-go-step]');
      if (stepButton && !stepButton.disabled) return this.goToStep(Number(stepButton.dataset.goStep));

      if (event.target.closest('[data-next]')) return this.goToStep(2);
      if (event.target.closest('[data-back]')) return this.goToStep(1);
      if (event.target.closest('[data-add]')) return this.addToCart();
    }

    readCard(card) {
      const escape = (text) => text.replace(/[&<>"']/g, (c) => `&#${c.charCodeAt(0)};`);
      return {
        id: Number(card.dataset.variantId),
        price: Number(card.dataset.price),
        comparePrice: Number(card.dataset.comparePrice) || 0,
        title: escape(card.dataset.title),
        image: card.dataset.image,
        scale: parseFloat(card.dataset.scale) || 1,
      };
    }

    selectChain(card) {
      this.chain = this.readCard(card);
      this.render();
    }

    toggleCharm(card) {
      const id = Number(card.dataset.variantId);
      const index = this.charms.findIndex((charm) => charm.id === id);
      if (index > -1) {
        this.charms.splice(index, 1);
      } else if (this.charms.length < this.maxCharms) {
        this.charms.push(this.readCard(card));
      }
      this.render();
    }

    removeItem(key) {
      if (key === 'chain') {
        this.chain = null;
        this.charms = [];
        this.goToStep(1);
      } else {
        this.charms = this.charms.filter((charm) => charm.id !== Number(key));
      }
      this.render();
    }

    showTab(id) {
      this.querySelectorAll('[data-tab]').forEach((tab) => tab.setAttribute('aria-selected', tab.dataset.tab === id));
      this.querySelectorAll('[data-tab-panel]').forEach((panel) => (panel.hidden = panel.dataset.tabPanel !== id));
    }

    goToStep(step) {
      if (step === 2 && !this.chain) return;
      this.dataset.step = step;
      this.querySelectorAll('[data-step-panel]').forEach((panel) => (panel.hidden = panel.dataset.stepPanel != step));
      this.render();
      const top = this.querySelector('.charm-bar__panel').getBoundingClientRect().top;
      if (top < 0) this.querySelector('.charm-bar__panel').scrollIntoView({ behavior: 'smooth' });
    }

    // Charm positions depend on how many are selected: 1 → center, 2 → between slots, 3 → left/center/right.
    getPositions(count) {
      const { left, center, right } = this.slots;
      const mid = (a, b) => [(a[0] + b[0]) / 2, (a[1] + b[1]) / 2];
      if (count === 1) return [[...center, 0]];
      if (count === 2) return [[...mid(left, center), this.rotation / 2], [...mid(center, right), -this.rotation / 2]];
      if (count === 3) return [[...left, this.rotation], [...center, 0], [...right, -this.rotation]];
      // More than 3: spread evenly between left and right.
      return Array.from({ length: count }, (_, i) => {
        const t = i / (count - 1);
        const x = left[0] + (right[0] - left[0]) * t;
        const y = center[1] - Math.abs(t - 0.5) * 2 * (center[1] - (left[1] + right[1]) / 2);
        return [x, y, this.rotation * (1 - 2 * t)];
      });
    }

    renderPreview() {
      const hasChain = Boolean(this.chain && this.chain.image);
      this.chainImg.hidden = !hasChain;
      this.placeholder.hidden = hasChain;
      this.classList.toggle('has-chain', hasChain);
      if (this.discountNote) this.discountNote.hidden = !(hasChain && this.chain.comparePrice > this.chain.price);
      if (!hasChain) {
        this.chainImg.removeAttribute('src');
        this.chainImg.alt = '';
      } else if (this.chainImg.getAttribute('src') !== this.chain.image) {
        this.chainImg.src = this.chain.image;
        this.chainImg.alt = this.chain.title;
      }

      const positions = this.getPositions(this.charms.length);
      this.charmLayer.innerHTML = this.charms
        .map((charm, i) => {
          const [x, y, rot] = positions[i];
          const width = this.charmWidth * charm.scale;
          return `<img class="charm-bar__charm" src="${charm.image}" alt="${charm.title}"
            style="left:${x}%;top:${y}%;width:${width}%;transform:translateX(-50%) rotate(${rot}deg)">`;
        })
        .join('');
    }

    renderSelection() {
      const row = (key, item) => `
        <li class="charm-bar__selection-item">
          <span>${item.title}</span>
          <span>${
            item.comparePrice > item.price ? `<s>${this.formatter.format(item.comparePrice / 100)}</s> ` : ''
          }${this.formatter.format(item.price / 100)}</span>
          <button type="button" class="charm-bar__remove" data-remove="${key}" aria-label="Quitar ${item.title}">×</button>
        </li>`;
      this.selectionList.innerHTML =
        (this.chain ? row('chain', this.chain) : '') + this.charms.map((charm) => row(charm.id, charm)).join('');

      const total = [this.chain, ...this.charms].filter(Boolean).reduce((sum, item) => sum + item.price, 0);
      this.totalEl.textContent = this.formatter.format(total / 100);
    }

    renderCards() {
      const chainId = this.chain && this.chain.id;
      const charmIds = this.charms.map((charm) => charm.id);
      const full = this.charms.length >= this.maxCharms;

      this.querySelectorAll('.charm-bar__card').forEach((card) => {
        const id = Number(card.dataset.variantId);
        const selected = card.dataset.kind === 'chain' ? id === chainId : charmIds.includes(id);
        card.setAttribute('aria-pressed', selected);
        if (card.dataset.kind === 'charm') card.classList.toggle('is-muted', full && !selected);
      });
    }

    render() {
      const step = Number(this.dataset.step);
      this.renderPreview();
      this.renderSelection();
      this.renderCards();

      this.querySelectorAll('[data-step-label]').forEach((label) =>
        label.classList.toggle('is-active', Number(label.dataset.stepLabel) === step)
      );
      this.backBtn.hidden = step === 1;
      this.nextBtn.hidden = step === 2;
      this.nextBtn.disabled = !this.chain;
      this.querySelector('[data-go-step="2"]').disabled = !this.chain;
      this.querySelectorAll('[data-go-step]').forEach((button) =>
        Number(button.dataset.goStep) === step
          ? button.setAttribute('aria-current', 'step')
          : button.removeAttribute('aria-current')
      );
      this.addBtn.hidden = step === 1;
      this.addBtn.disabled = !this.chain || this.charms.length === 0;
    }

    async addToCart() {
      if (!this.chain || this.charms.length === 0) return;

      const cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
      const code = Math.random().toString(36).slice(2, 8).toUpperCase();
      const properties = { [this.dataset.propertyName || 'Charm Bar']: `#${code}` };
      const items = [this.chain, ...this.charms].map((item) => ({ id: item.id, quantity: 1, properties }));

      this.errorEl.hidden = true;
      this.addBtn.disabled = true;
      this.addBtn.classList.add('loading');

      try {
        const body = { items };
        if (cart) {
          body.sections = cart.getSectionsToRender().map((section) => section.id);
          body.sections_url = window.location.pathname;
        }
        const response = await fetch(`${window.routes.cart_add_url}.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await response.json();

        if (!response.ok || data.status) {
          this.errorEl.textContent = data.description || data.message || 'Error';
          this.errorEl.hidden = false;
          return;
        }

        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'charm-bar', cartData: data });
        }

        // The items are already in the cart at this point. Shopify can answer with
        // `sections: null`, so go to the cart instead of failing to render the notification.
        if (!cart || !data.sections) {
          window.location = window.routes.cart_url;
          return;
        }
        cart.classList.remove('is-empty');
        cart.renderContents({ ...data.items[0], sections: data.sections });
      } catch (error) {
        console.error(error);
        this.errorEl.textContent = 'Error';
        this.errorEl.hidden = false;
      } finally {
        this.addBtn.classList.remove('loading');
        this.addBtn.disabled = false;
      }
    }
  }

  customElements.define('charm-bar', CharmBar);
}
